<template>
  <div>
    <a-row :gutter="24">
      <a-col :span="8">
        <div class="card">
          <div class="card-body">
            <h5 class="mb-4">
              <strong>Auction details</strong>
            </h5>

            <div class="mb-2">
              <status :auction="auction" />
            </div>
            <a-row class="mb-5">
              <countdown
                v-if="
                  ['canceled', 'completed', 'closed'].indexOf(
                    auction.status
                  ) === -1
                "
                @auctionStarted="startAuction"
                @auctionEnded="endAuction"
                :startTime="moment(auction.startsAt).unix()"
                :endTime="moment(auction.endsAt).unix()"
              ></countdown>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Auction status</a-col>
              <a-col :span="12" class="text-right">
                <a-tag
                  :color="auction.status == 'canceled' ? 'red' : '#108ee9'"
                >
                  <span class="text-uppercase">{{ auction.status }}</span>
                </a-tag>
              </a-col>
            </a-row>

            <a-row class="mb-2">
              <a-col :span="12">Auction direction</a-col>
              <a-col :span="12" class="text-right">
                <a-tag color="#070707">
                  <span class="text-uppercase">{{
                    auction.flow == 'buy' ? 'Purchasing' : 'Selling'
                  }}</span>
                </a-tag>
              </a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Auction type</a-col>
              <a-col :span="12" class="text-right">
                <a-tag color="#108ee9">
                  <span class="text-uppercase">{{ auction.type }}</span>
                </a-tag>
              </a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Auction start</a-col>
              <a-col :span="12" class="text-right">{{ auctionStart }}</a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Auction end</a-col>
              <a-col :span="12" class="text-right">{{ auctionEnd }}</a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Auction name</a-col>
              <a-col :span="12" class="text-right">{{ auction.name }}</a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Allocation volume</a-col>
              <a-col :span="12" class="text-right">
                <b>{{ auction.allocationVolume }}</b> MWh/h
              </a-col>
            </a-row>
            <a-row class="mb-2">
              <a-col :span="12">Required collateral</a-col>
              <a-col :span="12" class="text-right">
                <b>{{ auction.requiredCollateral }}</b> EUR
              </a-col>
            </a-row>

            <div class="mb-3 mt-3">
              <label for="">Auction comment</label>
              <p class="auction-comment">{{ auction.comment }}</p>
            </div>

            <a-row :gutter="16">
              <a-col :span="24">
                <LoadPattern
                  v-if="
                    this.auction.allocationPattern.length > 0 && !flatPattern
                  "
                  :pattern="this.auction.allocationPattern"
                  :shouldReact="false"
                ></LoadPattern>
              </a-col>
            </a-row>
          </div>
        </div>
      </a-col>

      <a-col :span="16">
        <div
          class="card"
          v-if="isSuperUser && this.auction.status == 'announced'"
        >
          <div class="card-body">
            <div>
              <h5 class="mb-4">
                <strong>Auction comment</strong>
              </h5>

              <div class="mb-3">
                <a-textarea
                  v-model="auction.comment"
                  :rows="4"
                  placeholder="Message visible to the customer on the auction screen"
                />
              </div>
              <a-space :space="16">
                <a-button type="primary" @click="updateComment"
                  >Update comment
                </a-button>
              </a-space>
            </div>
          </div>
        </div>

        <div
          class="card"
          v-if="isSuperUser && this.auction.status == 'announced'"
        >
          <div class="card-body">
            <h5 class="mb-4">
              <strong>Participants</strong>
            </h5>

            <p class="font-weight-light mb-3">
              You can select or exclude participants from the list before the
              auction start. Please note that email will be sent to all users of
              invited participants automatically.
            </p>
            <a-spin :spinning="loadingParticipants">
              <div style="">
                <a-table
                  :columns="participantColumns"
                  :data-source="participants"
                  :pagination="{ pageSize: 50 }"
                  :row-key="(record) => record.id"
                  :row-selection="{
                    selectedRowKeys: participantIds,
                    onChange: onParticipantSelectionChanged,
                  }"
                >
                </a-table>
              </div>
            </a-spin>
          </div>
        </div>

        <div class="card">
          <a-spin :spinning="finalizing">
            <div class="card-body">
              <div class="clearfix mb-5">
                <h5 class="mb-4">
                  <strong>Auction overview</strong>
                </h5>
                <div class="float-right">
                  <a-space :space="16">
                    <a-button
                      v-if="
                        isSuperUser &&
                        ['closed'].indexOf(this.auction.status) !== -1
                      "
                      type="primary"
                      @click="completeAuction"
                      >Finalize auction
                    </a-button>
                    <a-button
                      v-if="this.auction.status == 'live'"
                      @click="
                        () => (createOrderFormVisible = !createOrderFormVisible)
                      "
                      type="primary"
                      >Place New Order
                    </a-button>
                    <a-button
                      v-if="
                        isSuperUser &&
                        ['announced', 'live', 'closed'].indexOf(
                          this.auction.status
                        ) !== -1
                      "
                      type="danger"
                      @click="cancelAuction"
                      >Cancel auction
                    </a-button>
                  </a-space>
                </div>
              </div>

              <a-spin :spinning="creatingOrder">
                <div v-if="createOrderFormVisible">
                  <a-form-model
                    ref="bidForm"
                    :model="bid"
                    @submit="createBid"
                    :form="form"
                    layout="vertical"
                  >
                    <a-form-model-item
                      v-if="isSuperUser"
                      label="Participant"
                      ref="bidAmount"
                      prop="bidAmount"
                    >
                      <a-select
                        show-search
                        placeholder="Select participant"
                        option-filter-prop="children"
                        :filter-option="filterOption"
                        v-model="bid.participantId"
                        @change="handleParticipantChange"
                      >
                        <a-select-option
                          v-for="participant in assignedParticipants"
                          :value="participant.id"
                          :key="participant.id"
                        >
                          {{ participant.name }}
                        </a-select-option>
                      </a-select>
                    </a-form-model-item>

                    <a-row :gutter="16">
                      <a-col :span="12">
                        <a-form-model-item
                          label="Amount"
                          ref="bidAmount"
                          prop="bidAmount"
                        >
                          <a-input
                            type="number"
                            step="1"
                            v-model="bid.amount"
                            placeholder="Enter bid amount in MWh/h"
                          />
                        </a-form-model-item>
                      </a-col>
                      <a-col :span="12">
                        <a-form-model-item
                          label="Price per MWh"
                          ref="bidPrice"
                          prop="bidPrice"
                        >
                          <a-input
                            v-model="bid.price"
                            placeholder="Enter bid price per MWh/h in EUR"
                          ></a-input>
                          <span v-if="auction.minPrice" class="text-default"
                            >Min. {{ auction.minPrice }} EUR/MWh</span
                          >
                          <br />
                          <span v-if="auction.maxPrice" class="text-default"
                            >Max. {{ auction.maxPrice }} EUR/MWh</span
                          >
                        </a-form-model-item>
                      </a-col>
                    </a-row>

                    <a-form-item>
                      <a-button
                        class="btn btn-success pull-right"
                        @click="createBid"
                        >Place order</a-button
                      >
                    </a-form-item>
                  </a-form-model>
                </div>
              </a-spin>

              <OrdersGraph
                v-if="isSuperUser"
                :auction="auction"
                :orders="myOrders"
              />
            </div>
          </a-spin>
        </div>
      </a-col>
    </a-row>
    <a-row>
      <a-col :span="24">
        <a-spin :spinning="blockingOrders">
          <div class="card">
            <div class="card-body">
              <div class="">
                <h5 class="mb-4">
                  <strong>{{
                    isSuperUser ? 'All Orders' : 'My Orders'
                  }}</strong>
                </h5>
                <div class="float-right">
                  <a-space :space="16">
                    <a-button
                      class="mb-4"
                      v-if="
                        isSuperUser &&
                        ['completed'].indexOf(this.auction.status) !== -1
                      "
                      type="primary"
                      @click="downloadAuctionCsv"
                      >Generate CSV
                    </a-button>

                    <a-button
                      class="mb-4"
                      v-if="
                        isSuperUser &&
                        ['completed'].indexOf(this.auction.status) !== -1
                      "
                      type="primary"
                      @click="downloadAuctionPdf"
                      >Generate report
                    </a-button>
                  </a-space>
                </div>
              </div>

              <a-table
                :columns="columns"
                :dataSource="ordersCombined"
                rowKey="id"
              >
                <div slot="participantDateAndTime" slot-scope="text, record">
                  <div
                    :style="{
                      'padding-left':
                        record.type == 'participant' ? '0px' : '16px',
                    }"
                  >
                    {{ record.participantDateAndTime }}
                  </div>
                </div>
                <div slot="actions" slot-scope="text, record">
                  <a
                    v-if="
                      auction.status == 'live' && record.type != 'participant'
                    "
                    @click="cancelOrder(record.id)"
                    ><i class="fe fe-trash text-danger" title="Delete order"
                  /></a>

                  <a-space
                    v-if="
                      isSuperUser &&
                      auction.status == 'completed' &&
                      record.type == 'participant'
                    "
                    :space="16"
                  >
                    <a
                      class="text-danger"
                      @click="() => sendParticipantReport(record.participant)"
                      ><a-icon type="mail"></a-icon> Send PAR</a
                    >
                    <a
                      class="text-primary"
                      @click="
                        () => generateParticipantReport(record.participant.id)
                      "
                      ><a-icon type="file-pdf"></a-icon> Download PAR</a
                    >
                  </a-space>
                </div>
                <span
                  slot="status"
                  slot-scope="text"
                  :class="{
                    'font-size-12': true,
                    badge: true,
                    'badge-success': text === 'Accepted',
                    'badge-danger': text === 'Rejected',
                    'badge-warning': text === 'Partially accepted',
                  }"
                  >{{ text }}</span
                >
              </a-table>
            </div>
          </div>
        </a-spin>
      </a-col>
    </a-row>
  </div>
</template>
<script>
const columns = [
  {
    title: 'Participant / Date and time',
    dataIndex: 'participantDateAndTime',
    scopedSlots: { customRender: 'participantDateAndTime' },
  },
  {
    title: 'Quantity [MWh/h]',
    dataIndex: 'quantity',
  },
  {
    title: 'Amount allocated [MWh/h]',
    dataIndex: 'allocated',
  },
  {
    title: 'Price [EUR/MWh/h]',
    dataIndex: 'price',
    scopedSlots: { customRender: 'price' },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    scopedSlots: { customRender: 'status' },
  },
  {
    title: 'Actions',
    dataIndex: 'actions',
    scopedSlots: { customRender: 'actions' },
  },
]

const participantColumns = [
  {
    title: 'Name (Short name)',
    dataIndex: 'nameShortName',
    key: 'nameShortName',
  },
  {
    title: 'VAT Code',
    dataIndex: 'vatCode',
    key: 'right',
  },
  {
    title: 'Collateral',
    dataIndex: 'collateral',
    key: 'collateral',
    align: 'right',
  },
]

import moment from 'moment'
import { AuctionService, ParticipantService } from '@/services/api'
import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'
import LoadPattern from '@/components/belen/loadpattern'
import OrdersGraph from '@/components/belen/ordersgraph'

import { Space, Modal } from 'ant-design-vue'

Vue.use(Space)
Vue.use(Modal)

Vue.prototype.$confirm = Modal.confirm

import countdown from '@/components/belen/countdown'
import status from '@/components/belen/status'

export default {
  components: { countdown, status, LoadPattern, OrdersGraph },
  data: function () {
    return {
      finalizing: false,
      blockingOrders: false,
      auctionComment: '',
      createOrderFormVisible: false,
      currentAuctionId: null,
      columns,
      participantColumns,
      myOrders: [],
      form: this.$form.createForm(this),
      creatingOrder: false,
      loadingOrders: false,
      loadingParticipants: false,
      participants: [],
      participantIds: [],
      bid: {
        participantId: null,
        amount: null,
        price: null,
      },
      bidRules: {
        bidAmount: [
          {
            required: true,
            message: 'Please enter the amount that you want to bid for in MWh',
            trigger: 'change',
          },
        ],
        bidPrice: [
          {
            required: true,
            message: 'Please enter the price of your bid in EUR',
            trigger: 'change',
          },
        ],
      },
      auction: {
        id: '',
        status: 'created',
        type: '',
        flow: '',
        startsAt: '',
        openFor: '',
        endsAt: '',
        name: '',
        auctionVolume: '',
        comment: '',
        createdAt: '',
        updatedAt: '',
        requiredCollateral: 0,
        allocationPattern: [],
      },
    }
  },
  methods: {
    moment,
    startAuction() {
      if (this.auction.status != 'live') {
        this.$notification['info']({
          message: 'Auction starting',
          description: 'You will be redirected to bidding page soon...',
        })
      }
      // window.location.reload()
      // this.$router.push(`/auctions/${this.auction.id}/live`)
    },
    updateComment() {
      this.processing = true
      AuctionService.update(this.auction.id, { comment: this.auction.comment })
        .then((response) => {
          if (response.data.data) {
            this.processing = false
            this.$notification['info']({
              message: 'Info',
              description: 'Auction comment updated successfully.',
            })
          }
        })
        .catch((error) => {
          this.processing = false
          console.error(error)
          this.$notification['error']({
            message: 'Info',
            description: 'Failed to update auction comment. Please try again.',
          })
        })
    },
    onParticipantSelectionChanged(selectedRowKeys) {
      let notification = this.$notification
      let fetchAuction = this.fetchAuction
      let auctionId = this.currentAuctionId
      let that = this

      this.$confirm({
        title: 'Are you sure you want to update list of the participants?',
        okText: 'Update',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          that.loadingParticipants = true
          AuctionService.updateParticipants(auctionId, selectedRowKeys)
            .then((response) => {
              if (response.data.data) {
                notification['success']({
                  message: 'Participants updated successfully',
                })
              }
            })
            .catch((error) => {
              console.error(error)
              notification['error']({
                message: 'Participants list could not be altered',
                description: error.error,
              })
            })
            .finally(() => {
              that.loadingParticipants = false
              fetchAuction()
            })
        },
        onCancel() {},
      })
    },
    downloadAuctionCsv() {
      this.getCsv()
    },
    downloadAuctionPdf() {
      this.getPdf()
    },
    getPdf(participantId = null) {
      let auctionId = this.currentAuctionId
      // let notification = this.$notification
      let fetchAuction = this.fetchAuction

      AuctionService.getAuctionPdf(auctionId, participantId)
        .then((response) => {
          if (response.data.data) {
            // const url = window.URL.createObjectURL(new Blob([response.data]))
            const url = response.data.data.url
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = url
            // link.setAttribute('download', `AUCTION.pdf`)
            document.body.appendChild(link)
            link.click()
          }
        })
        .catch((error) => {
          console.error(error)
          notification['error']({
            message: 'PDF could not be retrieved',
            description: error.error,
          })
        })
        .finally(() => {
          fetchAuction()
        })
    },
    getCsv(participantId = null) {
      let auctionId = this.currentAuctionId
      let fetchAuction = this.fetchAuction

      AuctionService.getAuctionCsv(auctionId, participantId)
        .then((response) => {
          if (response.data.data) {
            // const url = window.URL.createObjectURL(new Blob([response.data]))
            const url = response.data.data.url
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = url
            // link.setAttribute('download', `AUCTION.pdf`)
            document.body.appendChild(link)
            link.click()
          }
        })
        .catch((error) => {
          console.error(error)
          notification['error']({
            message: 'CSV could not be retrieved',
            description: error.error,
          })
        })
        .finally(() => {
          fetchAuction()
        })
    },
    cancelOrder(orderId) {
      let auctionId = this.currentAuctionId
      let notification = this.$notification

      let that = this

      this.$confirm({
        title: `Are you sure you want to cancel order?`,
        okText: 'Yes, cancel order',
        okType: 'danger',
        cancelText: 'No',
        onOk() {
          that.blockingOrders = true
          AuctionService.cancelOrder(auctionId, orderId)
            .then((response) => {
              if (response.data.data) {
                notification['success']({
                  message: 'Order canceled successfully',
                })
              }
            })
            .catch((error) => {
              console.error(error)
              notification['error']({
                message: 'Order could not be canceled',
                description: error.error,
              })
            })
            .finally(() => {
              that.blockingOrders = false
              that.fetchMyOrders()
            })
        },
        onCancel() {},
      })
    },
    sendParticipantReport(participant) {
      let auctionId = this.currentAuctionId
      let notification = this.$notification
      let participantId = participant.id

      let that = this

      this.$confirm({
        title: `Are you sure you want to send PAR to ${participant.name}?`,
        okText: 'Send',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          that.blockingOrders = true
          AuctionService.sendParticipantReport(auctionId, participantId)
            .then((response) => {
              if (response.data.data) {
                notification['success']({
                  message: 'Report dispathed successfully',
                })
              }
            })
            .catch((error) => {
              console.error(error)
              notification['error']({
                message: 'Report could not be sent',
                description: error.error,
              })
            })
            .finally(() => {
              that.blockingOrders = false
            })
        },
        onCancel() {},
      })
    },
    generateParticipantReport(participantId) {
      this.getPdf(participantId)
    },
    endAuction() {},
    handleParticipantChange(value) {
      this.bid.participantId = value
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    resetForm() {
      this.$refs.bidForm.resetFields()
      this.bid.participantId = null
      this.bid.amount = null
      this.bid.price = null
    },
    completeAuction() {
      let auctionId = this.currentAuctionId
      let notification = this.$notification
      let fetchAuction = this.fetchAuction

      let that = this

      this.$confirm({
        title: 'Are you sure you want to finalize this auction?',
        okText: 'Finalize',
        okType: 'danger',
        cancelText: 'No, return to auction',
        onOk() {
          that.finalizing = true
          AuctionService.finalize(auctionId)
            .then((response) => {
              if (response.data.data) {
                notification['success']({
                  message: 'Auction finalized',
                  description: 'Auction has been finalized successfully.',
                })
              }
            })
            .catch((error) => {
              console.error(error)
              notification['error']({
                message: 'Auction could not be finalized',
                description: error.error,
              })
            })
            .finally(() => {
              that.finalizing = false
              fetchAuction()
            })
        },
        onCancel() {},
      })
    },
    cancelAuction() {
      let auctionId = this.currentAuctionId

      let notification = this.$notification
      let fetchAuction = this.fetchAuction

      this.$confirm({
        title: 'Are you sure you want to cancel this auction?',
        okText: 'Cancel',
        okType: 'danger',
        cancelText: 'No, return to auction',
        onOk() {
          AuctionService.cancel(auctionId)
            .then((response) => {
              if (response.data.data) {
                notification['success']({
                  message: 'Auction canceled',
                  description: 'Auction has been canceled successfully.',
                })
              }
            })
            .catch((error) => {
              console.error(error)
              notification['error']({
                message: 'Auction could not be canceled',
                description: error.error,
              })
            })
            .finally(() => {
              fetchAuction()
            })
        },
        onCancel() {},
      })
    },
    createBid(event, fromData) {
      this.$refs.bidForm.validate((valid) => {
        if (valid) {
          this.creatingOrder = true

          const orderData = {
            auctionId: this.currentAuctionId,
            amount: this.bid.amount,
            price: this.bid.price,
            participantId: this.bid.participantId,
          }

          AuctionService.createOrder(this.currentAuctionId, orderData)
            .then((response) => {
              if (response.data.data) {
                this.$notification['success']({
                  message: 'Bid placed successfully',
                  description: 'Your bid has been placed successfully.',
                })
                this.resetForm()
              }
            })
            .catch((error) => {
              console.error(error)
              this.$notification['error']({
                message: 'Bid was not accepted',
                description: error.error,
              })
            })
            .finally(() => {
              this.creatingOrder = false
              this.$store.dispatch('user/LOAD_CURRENT_ACCOUNT')
              this.fetchMyOrders()
            })
        } else {
          return false
        }
      })
    },
    fetchMyOrders() {
      this.loadingOrders = true
      AuctionService.getOrders(this.currentAuctionId)
        .then((response) => {
          this.myOrders = response.data.data
        })
        .catch((error) => {
          // show error
        })
        .finally(() => {
          this.loadingOrders = false
        })
    },
    async fetchAuction() {
      try {
        const response = await AuctionService.get(this.currentAuctionId)
        this.auction = response.data.data
      } catch (error) {
        console.error(error)
      } finally {
        this.participantIds = this.auction.participants.map(
          (participant) => participant.id
        )
        this.fetchMyOrders()
      }
    },
  },
  computed: {
    ...mapGetters('user', ['user']),
    assignedParticipants() {
      return this.participants.filter(function (participant) {
        console.log('My participants', this, participant)
        return this.indexOf(participant.id) >= 0
      }, this.participantIds)
    },
    flatPattern() {
      return (
        [...new Set(this.auction.allocationPattern.map((item) => item.volume))]
          .length == 1
      )
    },
    ordersCombined() {
      var perParticipant = {}
      this.myOrders.map((order) => {
        if (perParticipant[order.participantName]) {
          perParticipant[order.participantName].quantity += order.quantity
          perParticipant[order.participantName].allocated += order.allocated
          perParticipant[order.participantName].orders.push(
            Object.assign(order, { participantDateAndTime: order.receivedAt })
          )
        } else {
          perParticipant[order.participantName] = {
            id: 'P' + order.id,
            type: 'participant',
            quantity: order.quantity,
            allocated: order.allocated,
            participantName: order.participantName,
            participant: order.participant,
            isHeaderCol: true,
            orders: [
              Object.assign(order, {
                participantDateAndTime: order.receivedAt,
              }),
            ],
            participantDateAndTime: order.participantName,
          }
        }
      })

      const rows = []

      for (const [key, value] of Object.entries(perParticipant)) {
        rows.push(value)
        value.orders.map((order) => {
          rows.push(order)
        })
      }

      console.log(rows)

      return rows
    },
    isSuperUser() {
      return ['admin', 'operator'].includes(this.user.role)
    },
    auctionStart() {
      return this.moment(this.auction.startsAt).format('DD.MM.YYYY HH:mm')
    },
    auctionEnd() {
      return this.moment(this.auction.endsAt).format('DD.MM.YYYY HH:mm')
    },
  },
  async mounted() {
    this.currentAuctionId = this.$route.params.id
    // Fixing the issue with the auction not being fetched before the orders in the closed auctions
    // which caused reversal of the graph due to default auction direction (buy/sell)
    await this.fetchAuction()

    this.loadingParticipants = true
    ParticipantService.get().then((response) => {
      this.loadingParticipants = false
      this.participants = response.data.data
      // preselect all participants
    })

    this.fetchMyOrders()
    this.interval = setInterval(() => this.fetchAuction(), 5000)
  },
  beforeCreate() {
    this.form = this.$form.createForm(this, {})
  },
  destroyed() {
    clearInterval(this.interval)
  },
}
</script>

<style>
.card-body h5 {
  display: inline-block;
}

.ant-col.ant-form-item-label {
  padding-bottom: 0px;
}
.ant-col.ant-form-item-label label {
  margin-bottom: 0px;
}
.auction-comment {
  padding: 15px;
  background-color: #f1f4f8;
  border-radius: 3px;
}
</style>
